<template>
  <div class="find-contents">
    <div class="search-section">
      <el-input
        v-model="searchQuery"
        placeholder="Search contents..."
        prefix-icon="el-icon-search"
        clearable
        @clear="handleSearchClear"
        @input="handleSearch"
      >
      </el-input>
    </div>

    <el-tabs v-model="activeTab">
      <el-tab-pane label="Decks" name="contents">
        <el-radio-group v-model="contentType" class="filter-group">
          <!-- <el-radio-button label="all">All</el-radio-button> -->
        </el-radio-group>

        <div class="content-list">
          <el-row :gutter="20">
            <el-col 
              :xs="24" 
              :sm="12" 
              :lg="8" 
              v-for="collection in filteredCollections" 
              :key="collection._id.$id"
            >
              <collection-card
                :collection="{...collection.content_info, creator_name: collection.creator_name, creator_avatar_address_small: collection.creator_avatar_address_small}"
                @click.native="viewCollection(collection.knowit_id)"
              />
            </el-col>
          </el-row>

          <div v-if="!filteredCollections.length" class="no-results">
            <i class="el-icon-search"></i>
            <p>No collections found</p>
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane label="Bundles" name="bundles">
        <div class="bundle-list">
          <el-row :gutter="20">
            <el-col :xs="24" :sm="24" :md="12" :lg="12" v-for="bundle in filteredBundles" :key="bundle._id.$id">
              <el-card class="bundle-card" :body-style="{ padding: '0' }">
                <div class="bundle-layout">
                  <div class="bundle-header">
                    <div class="content-type-tag bundle">Bundle</div>
                    <span class="content-date">{{ formatDate(bundle.created_at) }}</span>
                  </div>
                  
                  <div class="bundle-main">
                    <div class="bundle-covers">
                      <div
                        v-for="(collection, index) in Object.values(bundle.content_info?.collection_array || {}).slice(0, 3)"
                        :key="index"
                        class="cover-item"
                        :style="{
                          backgroundImage: `url(${collection?.cover_url || '/default-cover.jpg'})`,
                          zIndex: 3 - index,
                          left: `${index * 15}px`
                        }"
                      ></div>
                      <div
                        v-if="!bundle.content_info?.collection_array"
                        class="cover-item"
                        :style="{ backgroundImage: `url('/default-cover.jpg')` }"
                      ></div>
                    </div>
                    <div class="bundle-details">
                      <h3 class="bundle-title">{{ bundle.title }}</h3>
                      <p class="bundle-description">{{ bundle.content_info?.description || '' }}</p>
                      <div class="creator-info" v-if="bundle.creator_name">
                        <span class="by-text">By</span>
                        <img
                          v-if="bundle.creator_avatar_address_small"
                          :src="bundle.creator_avatar_address_small"
                          class="creator-avatar"
                          :alt="bundle.creator_name"
                        />
                        <div v-else class="creator-avatar-placeholder">
                          {{ bundle.creator_name.charAt(0).toUpperCase() }}
                        </div>
                        <span class="creator-name">{{ bundle.creator_name }}</span>
                      </div>
                      <div class="bundle-stats">
                        <span class="items-count">
                          <i class="el-icon-collection"></i>
                          {{ Object.keys(bundle.content_info?.collection_array || {}).length || 0 }} items
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="bundle-footer">
                    <el-button type="primary" size="small" @click="viewDetail(bundle)">View Bundle</el-button>
                  </div>
                </div>
              </el-card>
            </el-col>
          </el-row>

          <div v-if="!filteredBundles.length" class="no-results">
            <i class="el-icon-search"></i>
            <p>No bundles found</p>
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'
import CollectionCard from '@/components/CollectionCard.vue'

export default {
  name: 'FindContents',
  components: {
    CollectionCard
  },
  data() {
    return {
      searchQuery: '',
      contentType: 'all',
      contents: [],
      bundles: [],
      loading: false,
      activeTab: 'contents'
    }
  },
  computed: {
    filteredContents() {
      let filtered = this.contents
      
      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(item => 
          item.title.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
        )
      }
      
      // Filter by content type
      if (this.contentType !== 'all') {
        filtered = filtered.filter(item => item.type === this.contentType)
      }
      
      return filtered
    },
    filteredBundles() {
      let filtered = this.bundles
      
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(item => 
          item.title.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
        )
      }
      
      return filtered
    },
    filteredCollections() {
      let filtered = this.contents
      
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(item => 
          item.title.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
        )
      }
      
      return filtered
    }
  },
  mounted() {
    this.fetchContents()
    this.fetchBundles()
  },
  methods: {
    async fetchContents() {
      try {
        this.loading = true
        const response = await postRequest('getActiveStoreContents', {})
        if (response.data.code === 0) {
          this.contents = response.data.data
        } else {
          this.$message.error('Failed to fetch contents')
        }
      } catch (error) {
        console.error('Error fetching contents:', error)
        this.$message.error('Failed to fetch contents')
      } finally {
        this.loading = false
      }
    },
    async fetchBundles() {
      try {
        this.loading = true
        const response = await postRequest('getActiveStoreContents', { type: 'bundle' })
        if (response.data.code === 0) {
          this.bundles = response.data.data
        } else {
          this.$message.error('Failed to fetch bundles')
        }
      } catch (error) {
        console.error('Error fetching bundles:', error)
        this.$message.error('Failed to fetch bundles')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      // Implement debounced search if needed
    },
    handleSearchClear() {
      this.searchQuery = ''
    },
    formatDate(timestamp) {
      if (!timestamp) return '-'
      return new Date(timestamp).toLocaleDateString()
    },
    getClubAvatarStyle(club) {
      return {
        backgroundColor: '#e6e6e6',
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#666'
      }
    },
    viewDetail(item) {
      if (item.type === 'bundle') {
        this.$router.push({
          name: 'Bundle Detail',
          query: {
            bundle_id: item.knowit_id
          }
        })
      } else {
        this.$router.push({
          name: 'Content Profile',
          query: {
            content_id: item.knowit_id,
            content_type: item.type
          }
        })
      }
    },
    viewCollection(collectionId) {
      this.$router.push({
        name: 'Content Profile',
        query: {
          content_id: collectionId,
          content_type: 'collection'
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.find-contents {
  padding: 20px;

  .search-section {
    margin-bottom: 24px;

    .el-input {
      max-width: 600px;
      margin-bottom: 16px;
    }

    .filter-group {
      margin-bottom: 16px;
    }
  }

  .content-list, .bundle-list {
    .content-card, .bundle-card {
      margin-bottom: 20px;
      transition: all 0.3s ease;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
      }

      .card-layout {
        display: flex;
        height: 252px;
      }

      .cover-image {
        flex: none;
        width: 180px;
        height: 252px;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
      }

      .card-content {
        flex: 1;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .content-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .content-title {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #2c3e50;
        line-height: 1.4;
      }

      .content-description {
        color: #5c6b7f;
        font-size: 14px;
        line-height: 1.6;
        flex-grow: 1;
        margin: 0;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .content-type-tag {
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        text-transform: capitalize;

        &.bundle {
          background-color: #ecf5ff;
          color: #409EFF;
        }
      }

      .bundle-info {
        margin: 0;
        
        .items-count {
          color: #409EFF;
          font-size: 14px;
          font-weight: 500;
        }
      }

      .content-footer {
        margin-top: auto;
        padding-top: 12px;
        border-top: 1px solid #edf2f7;
        display: flex;
        justify-content: flex-end;

        .el-button {
          padding: 0;
          font-weight: 500;
          
          &:hover {
            color: #66b1ff;
          }
        }
      }
    }
  }

  .no-results {
    text-align: center;
    padding: 40px;
    color: #909399;

    i {
      font-size: 48px;
      margin-bottom: 10px;
    }

    p {
      font-size: 16px;
    }
  }

  .text-truncate-3 {
    display: -webkit-box !important;
    -webkit-line-clamp: 3 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: normal !important;
    word-break: break-word !important;
    max-height: calc(1.6em * 3); // 3 lines based on line-height
  }

  .bundle-card {
    margin-bottom: 20px;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .bundle-layout {
      padding: 16px;
    }

    .bundle-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
    }

    .bundle-main {
      display: flex;
      gap: 16px;
      margin-bottom: 16px;
    }

    .bundle-covers {
      position: relative;
      width: 160px;
      height: 120px;
      flex-shrink: 0;

      .cover-item {
        position: absolute;
        width: 100px;
        height: 140px;
        border-radius: 8px;
        background-size: cover;
        background-position: center;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        transform: rotate(-5deg);
        transition: transform 0.3s ease;

        &:nth-child(2) {
          transform: rotate(0deg);
          left: 20px;
        }

        &:nth-child(3) {
          transform: rotate(5deg);
          left: 40px;
        }
      }
    }

    .bundle-details {
      flex: 1;
    }

    .bundle-title {
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 8px 0;
      color: #2c3e50;
    }

    .bundle-description {
      font-size: 14px;
      color: #5c6b7f;
      margin: 0 0 12px 0;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .creator-info {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;

      .by-text {
        font-size: 13px;
        color: #909399;
      }

      .creator-avatar {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid #e0e0e0;
      }

      .creator-avatar-placeholder {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: #e0e0e0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 600;
        color: #666;
        border: 2px solid #e0e0e0;
      }

      .creator-name {
        font-size: 13px;
        color: #909399;
      }
    }

    .bundle-stats {
      .items-count {
        color: #409EFF;
        font-size: 14px;
        font-weight: 500;
        
        i {
          margin-right: 4px;
        }
      }
    }

    .bundle-footer {
      border-top: 1px solid #edf2f7;
      padding-top: 16px;
      text-align: right;
    }

    &:hover {
      .cover-item {
        &:first-child {
          transform: rotate(-8deg) translateX(-8px);
        }
        
        &:nth-child(2) {
          transform: rotate(0deg) translateY(-5px);
        }
        
        &:nth-child(3) {
          transform: rotate(8deg) translateX(8px);
        }
      }
    }
  }
}
</style> 